import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card } from "@/components/ui/card";
import { ExternalLink, Shield, MapPin } from "lucide-react";

interface RegistrationLink {
  name: string;
  description: string;
  url: string;
}

interface Section {
  title: string;
  icon: string;
  links: RegistrationLink[];
}

const sections: Section[] = [
  {
    title: "Canada",
    icon: "🇨🇦",
    links: [
      {
        name: "Canadian Intellectual Property Office (CIPO) — Trademarks",
        description: "The official Canadian government office for registering trademarks. Search existing trademarks, file applications, and manage your registrations.",
        url: "https://ised-isde.canada.ca/site/canadian-intellectual-property-office/en/trademarks",
      },
      {
        name: "Canadian Trademarks Database",
        description: "Search the Canadian trademarks database to check if your desired mark is available before filing.",
        url: "https://ised-isde.canada.ca/cipo/trademark-search/srch?lang=eng",
      },
      {
        name: "CIPO — How to Register a Trademark",
        description: "Step-by-step guide from the Canadian government on the trademark registration process, fees, and timelines.",
        url: "https://ised-isde.canada.ca/site/canadian-intellectual-property-office/en/trademarks/how-register-trademark",
      },
      {
        name: "CIPO — Trademark Fees",
        description: "Complete fee schedule for trademark applications, renewals, and other services.",
        url: "https://ised-isde.canada.ca/site/canadian-intellectual-property-office/en/trademarks/trademark-fees",
      },
    ],
  },
  {
    title: "United States",
    icon: "🇺🇸",
    links: [
      {
        name: "United States Patent and Trademark Office (USPTO) — Trademarks",
        description: "The official US government office for trademark registration. File applications, search the trademark database, and access resources for business owners.",
        url: "https://www.uspto.gov/trademarks",
      },
      {
        name: "USPTO — Trademark Electronic Search System (TESS)",
        description: "Search the USPTO trademark database to see if a similar mark already exists before filing your application.",
        url: "https://tmsearch.uspto.gov/",
      },
      {
        name: "USPTO — Trademark Electronic Application System (TEAS)",
        description: "File your trademark application online through the USPTO's electronic filing system.",
        url: "https://www.uspto.gov/trademarks/apply",
      },
      {
        name: "USPTO — Trademark Basics",
        description: "Educational resource covering what trademarks are, why they matter, and how the registration process works.",
        url: "https://www.uspto.gov/trademarks/basics",
      },
      {
        name: "USPTO — Trademark Fees",
        description: "Current fee schedule for trademark applications, statements of use, renewals, and other services.",
        url: "https://www.uspto.gov/trademarks/fees",
      },
    ],
  },
];

export default function TrademarkRegistrations() {
  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Founder Resources</span>
              </div>
              <h1 className="text-2xl sm:text-4xl md:text-6xl font-display font-bold mb-6" data-testid="text-trademark-title">Trademark Registrations</h1>
              <p className="text-xl text-muted-foreground max-w-2xl">
                Official government links for registering a trademark in Canada and the United States.
              </p>
            </div>

            {sections.map((section) => (
              <div key={section.title} className="mb-16">
                <h2 className="text-2xl font-display font-bold mb-8 flex items-center gap-3">
                  <span className="text-3xl" aria-hidden="true">{section.icon}</span>
                  {section.title}
                </h2>

                <div className="space-y-3" role="list" aria-label={`${section.title} trademark registration links`}>
                  {section.links.map((link) => (
                    <Card key={link.url} role="listitem" className="border-white/5 p-5 hover:border-primary/20 transition-all">
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-start gap-4 group"
                        data-testid={`link-tm-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}
                      >
                        <Shield size={18} className="text-primary shrink-0 mt-1" aria-hidden="true" />
                        <div className="flex-1">
                          <h4 className="font-semibold group-hover:text-primary transition-colors flex items-center gap-2">
                            {link.name}
                            <ExternalLink size={14} className="text-muted-foreground" aria-hidden="true" />
                            <span className="sr-only">(opens in new tab)</span>
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">{link.description}</p>
                        </div>
                      </a>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
